/**
 * Page de connexion, en plusieurs pas.
 *
 *   1. email + mot de passe
 *   2. (si l'API le demande) nouveau mot de passe — trop vieux, ou en deçà de
 *      la politique
 *   3. second facteur : enrôlement (QR code) la première fois, puis code à six
 *      chiffres — sauf appareil de confiance
 *
 * L'API renvoie volontairement le même message pour un email inconnu et un
 * mot de passe faux : on l'affiche tel quel plutôt que d'essayer de le
 * préciser. Un ticket expiré (10 min) renvoie au premier pas.
 */

import { PASSWORD_POLICY_TEXT, TRUSTED_DEVICE_DAYS, passwordPolicyIssues, type LoginStep } from '@shared/types/auth';
import QRCode from 'qrcode';
import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { useAuth } from '../hooks/useAuth';
import { ApiRequestError } from '../lib/api';

type Pending = Exclude<LoginStep, { step: 'ok' }>;

function Title() {
  return (
    <h1 className="login__title">
      <span className="app-header__dot" aria-hidden="true" />
      Piano LED Analytics
    </h1>
  );
}

function ErrorLine({ error }: { error: string | null }) {
  // role="alert" : le message est annonce des son apparition, sans que
  // l'utilisateur ait a repartir le chercher.
  return error ? (
    <p className="login__error" role="alert">
      {error}
    </p>
  ) : null;
}

/** QR code du secret TOTP, rendu en data-URL ; le secret en clair à côté. */
function MfaSetup({ otpauthUrl, secret }: { otpauthUrl: string; secret: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(otpauthUrl, { margin: 1, width: 196, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => setDataUrl(null));
    return () => {
      cancelled = true;
    };
  }, [otpauthUrl]);
  const grouped = secret.match(/.{1,4}/g)?.join(' ') ?? secret;
  return (
    <div className="login__mfa-setup">
      {dataUrl ? <img className="login__qr" src={dataUrl} alt="QR code à scanner dans l'application d'authentification" width={196} height={196} /> : null}
      <p className="login__hint">
        Scanne ce QR code avec une application d'authentification (Google Authenticator, Authy, 1Password, Bitwarden…), ou saisis la clé à la
        main :
      </p>
      <code className="login__secret">{grouped}</code>
    </div>
  );
}

export function LoginPage() {
  const { signIn, completePasswordChange, completeMfa, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('');
  const [code, setCode] = useState('');
  const [remember, setRemember] = useState(true);
  const [pending, setPending] = useState<Pending | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);

  if (isAuthenticated) return <Navigate to="/" replace />;

  /** Applique le pas renvoyé par l'API : session ouverte, ou pas suivant. */
  const advance = (step: LoginStep) => {
    if (step.step === 'ok') {
      navigate('/', { replace: true });
      return;
    }
    setPending(step);
    setCode('');
    setNewPassword('');
    setNewPasswordConfirm('');
  };

  const run = async (action: () => Promise<LoginStep>) => {
    setError(null);
    setSubmitting(true);
    try {
      advance(await action());
    } catch (caught) {
      // Ticket expire ou invalide : retour au premier pas, avec le message.
      if (caught instanceof ApiRequestError && caught.status === 401 && pending) setPending(null);
      setError(caught instanceof Error ? caught.message : 'Connexion impossible');
    } finally {
      setSubmitting(false);
    }
  };

  const submitCredentials = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    void run(() => signIn(email, password));
  };

  const submitPassword = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pending || pending.step !== 'password_change') return;
    const issues = passwordPolicyIssues(newPassword);
    if (issues.length > 0) return setError(`Il manque : ${issues.join(', ')}.`);
    if (newPassword !== newPasswordConfirm) return setError('Les deux saisies ne correspondent pas.');
    const { ticket } = pending;
    void run(() => completePasswordChange(ticket, newPassword));
  };

  const submitCode = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!pending || (pending.step !== 'mfa' && pending.step !== 'mfa_setup')) return;
    const { ticket } = pending;
    void run(() => completeMfa(ticket, code, remember));
  };

  const restart = () => {
    setPending(null);
    setError(null);
    setPassword('');
  };

  let form;
  if (pending === null) {
    form = (
      <>
        <p className="login__sub">Accès réservé. Comptes créés à la main sur le serveur.</p>
        <form className="login__form" onSubmit={submitCredentials}>
          <ErrorLine error={error} />
          <div className="field">
            <label className="field__label" htmlFor="email">
              Email
            </label>
            <input id="email" type="email" autoComplete="username" required value={email} onChange={(event) => setEmail(event.target.value)} />
          </div>
          <div className="field">
            <label className="field__label" htmlFor="password">
              Mot de passe
            </label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
            {isSubmitting ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>
      </>
    );
  } else if (pending.step === 'password_change') {
    form = (
      <>
        <p className="login__sub">
          {pending.reason === 'expired'
            ? 'Ton mot de passe a plus d’un an : il faut en choisir un nouveau.'
            : 'Ton mot de passe ne respecte plus la politique en vigueur : il faut en choisir un nouveau.'}
        </p>
        <form className="login__form" onSubmit={submitPassword}>
          <ErrorLine error={error} />
          <div className="field">
            <label className="field__label" htmlFor="new-password">
              Nouveau mot de passe
            </label>
            <input
              id="new-password"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
            />
            <span className="login__hint">{PASSWORD_POLICY_TEXT}</span>
          </div>
          <div className="field">
            <label className="field__label" htmlFor="new-password-confirm">
              Confirmer
            </label>
            <input
              id="new-password-confirm"
              type="password"
              autoComplete="new-password"
              required
              value={newPasswordConfirm}
              onChange={(event) => setNewPasswordConfirm(event.target.value)}
            />
          </div>
          <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
            {isSubmitting ? 'Enregistrement...' : 'Enregistrer et continuer'}
          </button>
          <button type="button" className="btn btn--link" onClick={restart}>
            Annuler
          </button>
        </form>
      </>
    );
  } else {
    const enrolling = pending.step === 'mfa_setup';
    form = (
      <>
        <p className="login__sub">
          {enrolling
            ? 'Première connexion : active le second facteur, il sera demandé à chaque connexion.'
            : 'Saisis le code de ton application d’authentification.'}
        </p>
        <form className="login__form" onSubmit={submitCode}>
          <ErrorLine error={error} />
          {enrolling && <MfaSetup otpauthUrl={pending.otpauthUrl} secret={pending.secret} />}
          <div className="field">
            <label className="field__label" htmlFor="mfa-code">
              Code à six chiffres
            </label>
            <input
              id="mfa-code"
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9 ]*"
              maxLength={7}
              required
              autoFocus
              value={code}
              onChange={(event) => setCode(event.target.value)}
            />
          </div>
          <label className="login__remember">
            <input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} />
            Ne plus demander de code sur cet appareil pendant {TRUSTED_DEVICE_DAYS} jours
          </label>
          <button type="submit" className="btn btn--primary" disabled={isSubmitting}>
            {isSubmitting ? 'Vérification...' : enrolling ? 'Activer et se connecter' : 'Se connecter'}
          </button>
          <button type="button" className="btn btn--link" onClick={restart}>
            Annuler
          </button>
        </form>
      </>
    );
  }

  return (
    <div className="login">
      <div className="login__card">
        <Title />
        {form}
      </div>
    </div>
  );
}
